import React, { useState, useRef, useEffect } from 'react';
import {
  User,
  Briefcase,
  Camera,
  ShieldCheck,
  FileText,
  Home,
  MapPin,
  CheckCircle2,
  AlertCircle,
  Upload,
  RefreshCw,
  Trash2,
  Crop,
  ChevronRight,
  ChevronLeft,
  Lock,
  Mail,
  Phone,
  Sparkles,
  Check,
  Navigation,
  Eye,
  Info,
  Loader2,
  X,
} from 'lucide-react';
import { SERVICE_CATEGORIES } from '../../data/categories';
import { NIGERIAN_STATES, NIGERIAN_LGAS_MAP } from '../../data/nigerianLocations';
import { useApp } from '../../context/AppContext';
import { GoogleSignInButton } from './GoogleSignInButton';

export interface UploadedDocState {
  fileName: string;
  mimeType: string;
  dataUrlOrBase64: string;
  sizeBytes: number;
  isPdf: boolean;
}

interface ProApplicationWizardProps {
  onSwitchToLogin: () => void;
}

const ACCEPTED_ID_TYPES = [
  { value: 'nin_slip', label: 'NIN Slip (National Identity Number Slip)' },
  { value: 'national_id_card', label: 'National Identity Card (NIMC Smart Card)' },
  { value: 'international_passport', label: 'Nigerian International Passport' },
  { value: 'drivers_license', label: 'FRSC National Driver’s License' },
  { value: 'voters_card', label: 'INEC Permanent Voter’s Card (PVC)' },
] as const;

const ACCEPTED_UTILITY_TYPES = [
  { value: 'electricity_bill', label: 'Electricity Bill (EKEDC / IKEDC / AEDC / PHED / IBEDC / EEDC)' },
  { value: 'water_bill', label: 'State Water Corporation Bill' },
  { value: 'waste_lawma_bill', label: 'Waste Management Bill (LAWMA / State Environmental Agency)' },
  { value: 'tenancy_agreement', label: 'Stamped Tenancy Agreement / Official Proof of Address' },
] as const;

const STEP_DEFINITIONS = [
  { number: 1, title: 'Personal Details', short: 'Personal', icon: User },
  { number: 2, title: 'Professional Details', short: 'Trade & Skills', icon: Briefcase },
  { number: 3, title: 'Profile Photograph', short: 'Profile Photo', icon: Camera },
  { number: 4, title: 'Verification Selfie', short: 'Live Selfie', icon: User },
  { number: 5, title: 'National ID Card', short: 'National ID', icon: ShieldCheck },
  { number: 6, title: 'Utility Bill', short: 'Utility Bill', icon: FileText },
  { number: 7, title: 'Service Location', short: 'Location & GPS', icon: MapPin },
  { number: 8, title: 'Review & Submit', short: 'Review', icon: CheckCircle2 },
];

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB

/**
 * Client-side magic-byte and MIME validator to catch invalid or spoofed files early
 * before server-side verification.
 */
async function validateClientFile(
  file: File,
  allowPdf: boolean
): Promise<{ valid: boolean; error?: string; doc?: UploadedDocState }> {
  if (!file) {
    return { valid: false, error: 'No file selected.' };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      valid: false,
      error: `File "${file.name}" is ${(file.size / (1024 * 1024)).toFixed(2)} MB, which exceeds the 5 MB maximum limit.`,
    };
  }
  if (file.size < 64) {
    return {
      valid: false,
      error: `File "${file.name}" appears to be empty or corrupted.`,
    };
  }

  const allowedTypes = allowPdf
    ? ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'application/pdf']
    : ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];

  const lowerType = (file.type || '').toLowerCase();
  if (!allowedTypes.includes(lowerType)) {
    return {
      valid: false,
      error: `Unsupported file type "${file.type || file.name}". Please upload a valid ${
        allowPdf ? 'JPEG, PNG, WebP image or PDF document' : 'JPEG, PNG, or WebP photograph'
      }.`,
    };
  }

  // Inspect first 12 bytes using ArrayBuffer
  const headerBuffer = await file.slice(0, 12).arrayBuffer();
  const bytes = new Uint8Array(headerBuffer);

  const isJpeg = bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff;
  const isPng =
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47;
  const isWebp =
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50;
  const isPdf =
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d;

  if (!isJpeg && !isPng && !isWebp && !(allowPdf && isPdf)) {
    return {
      valid: false,
      error: `File "${file.name}" failed binary signature verification. Renamed or unsupported files cannot be uploaded.`,
    };
  }

  if ((isPdf && !lowerType.includes('pdf')) || (!isPdf && lowerType.includes('pdf'))) {
    return {
      valid: false,
      error: `File "${file.name}" has a mismatched file extension and binary content.`,
    };
  }

  const detectedMime = isJpeg
    ? 'image/jpeg'
    : isPng
      ? 'image/png'
      : isWebp
        ? 'image/webp'
        : 'application/pdf';

  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

  return {
    valid: true,
    doc: {
      fileName: file.name,
      mimeType: detectedMime,
      dataUrlOrBase64: dataUrl,
      sizeBytes: file.size,
      isPdf,
    },
  };
}

export const ProApplicationWizard: React.FC<ProApplicationWizardProps> = ({ onSwitchToLogin }) => {
  const {
    pendingGoogleAuth,
    setPendingGoogleAuth,
    initiateGoogleSignIn,
    selectRoleForGoogleUser,
  } = useApp();
  const [googleLoading, setGoogleLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [stepError, setStepError] = useState<string | null>(null);

  const isGoogleProOnboarding = Boolean(
    pendingGoogleAuth?.googleUser?.id &&
      (pendingGoogleAuth.status === 'requires_onboarding' ||
        pendingGoogleAuth.status === 'requires_profile_completion')
  );

  // Step 1 — Personal Details
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneLocal, setPhoneLocal] = useState('');
  const [state, setState] = useState('Lagos');
  const [lga, setLga] = useState('Ikeja');
  const [town, setTown] = useState('');
  const [residentialAddress, setResidentialAddress] = useState('');
  const [preferredContactMethod, setPreferredContactMethod] = useState<'phone' | 'whatsapp' | 'email' | 'sms'>('phone');

  // Sequential OTP Verification State (Step 1: Stage 3 Email OTP -> Stage 4 Phone SMS OTP)
  const [registrationSessionId, setRegistrationSessionId] = useState('');
  const [verificationProofToken, setVerificationProofToken] = useState('');
  const [activeVerificationStage, setActiveVerificationStage] = useState<'email' | 'phone'>('email');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [phoneOtpSent, setPhoneOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpCooldown, setOtpCooldown] = useState(0);
  const [emailVerifiedViaOtp, setEmailVerifiedViaOtp] = useState(false);
  const [phoneVerifiedViaOtp, setPhoneVerifiedViaOtp] = useState(false);
  const [otpStatusMessage, setOtpStatusMessage] = useState<string | null>(null);

  // Step 2 — Professional Details
  const [primaryCategory, setPrimaryCategory] = useState('plumber');
  const [secondaryCategories, setSecondaryCategories] = useState<string[]>([]);
  const [additionalSkills, setAdditionalSkills] = useState<string[]>([]);
  const [skillInput, setSkillInput] = useState('');
  const [yearsExperience, setYearsExperience] = useState<number>(5);
  const [businessName, setBusinessName] = useState('');
  const [professionalDescription, setProfessionalDescription] = useState('');
  const [serviceType, setServiceType] = useState<'home_based' | 'business_based' | 'mobile'>('mobile');
  const [serviceCoverageAreas, setServiceCoverageAreas] = useState<string[]>(['Ikeja']);
  const [matchingRadiusKm, setMatchingRadiusKm] = useState<number>(15);
  const [hourlyRate, setHourlyRate] = useState<number>(8000);

  // Step 3 — Mandatory Profile Photograph + Interactive Crop
  const [profilePhoto, setProfilePhoto] = useState<UploadedDocState | null>(null);
  const [rawProfilePhotoUrl, setRawProfilePhotoUrl] = useState<string | null>(null);
  const [profilePhotoCropped, setProfilePhotoCropped] = useState(false);
  const [showCropModal, setShowCropModal] = useState(false);
  const [cropZoom, setCropZoom] = useState<number>(1.2);
  const [cropOffsetX, setCropOffsetX] = useState<number>(0);
  const [cropOffsetY, setCropOffsetY] = useState<number>(0);

  // Camera Stream State (shared by Step 3 Profile Photo & Step 4 Live Selfie)
  const [activeCameraMode, setActiveCameraMode] = useState<'profile' | 'selfie' | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);

  // Step 4 — Mandatory Selfie
  const [verificationSelfie, setVerificationSelfie] = useState<UploadedDocState | null>(null);
  const [selfieCaptureMethod, setSelfieCaptureMethod] = useState<'camera' | 'file_upload'>('camera');

  // Step 5 — National Identity Card
  const [idDocumentType, setIdDocumentType] = useState<string>('national_id_card');
  const [idDocumentFile, setIdDocumentFile] = useState<UploadedDocState | null>(null);
  const [maskedIdReference, setMaskedIdReference] = useState('');

  // Step 6 — Utility Bill
  const [utilityBillType, setUtilityBillType] = useState<string>('electricity_bill');
  const [utilityBillFile, setUtilityBillFile] = useState<UploadedDocState | null>(null);
  const [utilityBillIssueMonth, setUtilityBillIssueMonth] = useState('');

  // Step 7 — Home or Business Location & GPS
  const [locationType, setLocationType] = useState<'home' | 'business' | 'both'>('both');
  const [gpsPermissionState, setGpsPermissionState] = useState<'granted' | 'denied' | 'unavailable' | 'manual'>('manual');
  const [latitude, setLatitude] = useState<number | null>(null);
  const [longitude, setLongitude] = useState<number | null>(null);
  const [gpsAccuracyMeters, setGpsAccuracyMeters] = useState<number | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);
  const [gpsMessage, setGpsMessage] = useState<string | null>(null);
  const [pinAdjustedOnMap, setPinAdjustedOnMap] = useState(false);
  const [pinConfirmed, setPinConfirmed] = useState(false);
  const [serviceState, setServiceState] = useState('Lagos');
  const [serviceLga, setServiceLga] = useState('Ikeja');
  const [serviceTown, setServiceTown] = useState('');
  const [serviceStreetAddress, setServiceStreetAddress] = useState('');

  // Step 8 — Consent & Submission State
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionError, setSubmissionError] = useState<string | null>(null);
  const [retryToken, setRetryToken] = useState<string | null>(null);
  const [submittedSuccess, setSubmittedSuccess] = useState<{
    applicationReference: string;
    submittedAt: string;
  } | null>(null);

  const availableResidentialLgas = NIGERIAN_LGAS_MAP[state] || ['Ikeja'];
  const availableServiceLgas = NIGERIAN_LGAS_MAP[serviceState] || ['Ikeja'];

  // Stop camera stream on unmount or step change
  const stopCameraStream = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach((track) => track.stop());
      mediaStreamRef.current = null;
    }
    setActiveCameraMode(null);
  };

  useEffect(() => {
    return () => {
      stopCameraStream();
    };
  }, []);

  // Sync default service state/LGA/town from residential if empty when entering Step 7
  useEffect(() => {
    if (currentStep === 7) {
      if (!serviceTown && town) setServiceTown(town);
      if (!serviceStreetAddress && residentialAddress && locationType !== 'business') {
        setServiceStreetAddress(residentialAddress);
      }
    }
  }, [currentStep]);

  const formatPhoneWithCountryCode = (raw: string): string => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('234')) return `+${digits}`;
    if (digits.startsWith('0')) return `+234${digits.slice(1)}`;
    return `+234${digits}`;
  };

  // Restore non-sensitive registration draft on mount so page refreshes resume safely
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('jobmatch_pro_wizard_draft');
      if (!raw) return;
      const draft = JSON.parse(raw);
      if (draft.fullName) setFullName(draft.fullName);
      if (draft.email) setEmail(draft.email);
      if (draft.phoneLocal) setPhoneLocal(draft.phoneLocal);
      if (draft.state) setState(draft.state);
      if (draft.lga) setLga(draft.lga);
      if (draft.town) setTown(draft.town);
      if (draft.residentialAddress) setResidentialAddress(draft.residentialAddress);
      if (draft.registrationSessionId) {
        setRegistrationSessionId(draft.registrationSessionId);
        // Verify server-side session state before restoring verified flags
        fetch(`/api/auth/registration/session?sessionId=${encodeURIComponent(draft.registrationSessionId)}`)
          .then((res) => res.json())
          .then((data) => {
            if (data?.success && data?.session) {
              setEmailVerifiedViaOtp(Boolean(data.session.emailVerified));
              setPhoneVerifiedViaOtp(Boolean(data.session.phoneVerified));
              if (data.session.emailVerified && !data.session.phoneVerified) {
                setActiveVerificationStage('phone');
                setEmailOtpSent(true);
              }
              if (data.session.emailVerified && data.session.phoneVerified && draft.verificationProofToken) {
                setVerificationProofToken(draft.verificationProofToken);
              }
            }
          })
          .catch(() => {});
      }
    } catch {
      // Ignore storage errors
    }
  }, []);

  // Pre-fill Step 1 when returning from Google OAuth for Professional Registration
  useEffect(() => {
    if (isGoogleProOnboarding && pendingGoogleAuth?.googleUser) {
      if (pendingGoogleAuth.googleUser.fullName && !fullName) {
        setFullName(pendingGoogleAuth.googleUser.fullName);
      }
      if (pendingGoogleAuth.googleUser.email) {
        setEmail(pendingGoogleAuth.googleUser.email);
      }
      setEmailVerifiedViaOtp(true);
      setActiveVerificationStage('phone');
    }
  }, [isGoogleProOnboarding, pendingGoogleAuth]);

  const handleGoogleProRegisterClick = async () => {
    setStepError(null);
    setGoogleLoading(true);
    try {
      await initiateGoogleSignIn({
        role: 'professional',
        authMode: 'register',
      });
    } catch (err: any) {
      setStepError(err?.message || 'Authentication failed. Could not start Google Sign-In.');
      setGoogleLoading(false);
    }
  };

  // Persist non-sensitive Step 1 registration draft
  useEffect(() => {
    try {
      sessionStorage.setItem(
        'jobmatch_pro_wizard_draft',
        JSON.stringify({
          fullName,
          email,
          phoneLocal,
          state,
          lga,
          town,
          residentialAddress,
          registrationSessionId,
          verificationProofToken,
        })
      );
    } catch {
      // Ignore storage quota errors
    }
  }, [
    fullName,
    email,
    phoneLocal,
    state,
    lga,
    town,
    residentialAddress,
    registrationSessionId,
    verificationProofToken,
  ]);

  // Countdown timer for OTP resend cooldown
  useEffect(() => {
    if (otpCooldown <= 0) return;
    const timer = setInterval(() => {
      setOtpCooldown((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(timer);
  }, [otpCooldown]);

  // Step 1: Send Unified OTP to both Email and Phone SMS simultaneously
  const handleSendVerificationOtp = async (_stageOverride?: 'email' | 'phone') => {
    setStepError(null);
    setOtpStatusMessage(null);
    const formattedPhone = formatPhoneWithCountryCode(phoneLocal);
    if (!fullName.trim() || fullName.trim().length < 3) {
      setStepError('Please enter your full legal name before requesting a verification code.');
      return;
    }
    if (!email.includes('@') || formattedPhone.length < 13) {
      setStepError('Please enter a valid email address and Nigerian phone number (+234) before requesting a verification code.');
      return;
    }

    setOtpSending(true);

    try {
      const endpoint =
        isGoogleProOnboarding && !registrationSessionId
          ? '/api/auth/registration/google-phone-otp/send'
          : '/api/auth/registration/email-otp/send';
      const payload = {
        sessionId: registrationSessionId || undefined,
        role: 'professional',
        fullName: fullName.trim(),
        email: email.trim().toLowerCase(),
        phone: formattedPhone,
        ...(isGoogleProOnboarding ? { googleUserId: pendingGoogleAuth?.googleUser?.id } : {}),
      };

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        if (data.retryAfterSeconds) setOtpCooldown(Number(data.retryAfterSeconds));
        if (data.cooldownSeconds) setOtpCooldown(Number(data.cooldownSeconds));
        setStepError(data.message || data.error || 'Could not send verification code. Please check your email and phone number.');
        return;
      }
      if (data.sessionId) setRegistrationSessionId(data.sessionId);
      setEmailOtpSent(true);
      setPhoneOtpSent(true);
      setOtpCode('');
      setOtpCooldown(Number(data.cooldownSeconds || 60));
      setOtpStatusMessage(
        data.message ||
          `We sent the same 6-digit verification code to ${email.trim().toLowerCase()} and ${formattedPhone}. Enter the code from whichever arrives first.`
      );
    } catch {
      setStepError('Network error while requesting verification code. Please check your connection and try again.');
    } finally {
      setOtpSending(false);
    }
  };

  // Step 1: Verify Unified OTP (from either Email or SMS) in one step
  const handleVerifyOtp = async () => {
    const cleanOtp = otpCode.replace(/\D/g, '');
    if (cleanOtp.length !== 6) {
      setStepError('Please enter the 6-digit verification code.');
      return;
    }
    setOtpVerifying(true);
    setStepError(null);
    try {
      const formattedPhone = formatPhoneWithCountryCode(phoneLocal);
      const res = await fetch('/api/auth/registration/email-otp/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: registrationSessionId,
          email: email.trim().toLowerCase(),
          phone: formattedPhone,
          otp: cleanOtp,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        setStepError(data.message || data.error || 'Invalid or expired verification code. Please check your Email or SMS and try again.');
        return;
      }

      setEmailVerifiedViaOtp(true);
      setPhoneVerifiedViaOtp(true);
      if (data.verificationProofToken) {
        setVerificationProofToken(data.verificationProofToken);
      }
      setOtpCode('');
      setOtpStatusMessage(
        'Verification complete! Your Email and Phone Number are verified. You may now continue to Step 2 of your professional onboarding.'
      );
    } catch {
      setStepError('Could not verify OTP code at this moment. Please check your connection.');
    } finally {
      setOtpVerifying(false);
    }
  };


  // Start Camera for Profile Photo or Selfie
  const startCamera = async (mode: 'profile' | 'selfie') => {
    setCameraError(null);
    setStepError(null);
    stopCameraStream();

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setCameraError(
        'Direct browser camera access is not available on this device/browser. Please use the "Select / Capture Photo" file button below.'
      );
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'user',
          width: { ideal: 720 },
          height: { ideal: 720 },
        },
        audio: false,
      });
      mediaStreamRef.current = stream;
      setActiveCameraMode(mode);
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.play().catch(() => {});
        }
      }, 100);
    } catch (err: any) {
      setCameraError(
        `Camera permission was denied or unavailable (${err?.message || 'Camera access blocked'}). You can upload or take a photo using the file selector below.`
      );
    }
  };

  const captureFrameFromVideo = (mode: 'profile' | 'selfie') => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    const size = Math.min(video.videoWidth || 640, video.videoHeight || 640);
    canvas.width = 600;
    canvas.height = 600;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const sx = ((video.videoWidth || 640) - size) / 2;
    const sy = ((video.videoHeight || 640) - size) / 2;
    ctx.drawImage(video, sx, sy, size, size, 0, 0, 600, 600);

    // Add subtle timestamp metadata pixel variation so live selfie & profile captures are distinct binary buffers
    ctx.fillStyle = mode === 'selfie' ? 'rgba(5, 150, 105, 0.01)' : 'rgba(15, 23, 42, 0.01)';
    ctx.fillRect(0, 0, 2, 2);

    const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
    const approxBytes = Math.round((dataUrl.length * 3) / 4);

    const doc: UploadedDocState = {
      fileName: mode === 'profile' ? `profile_photo_${Date.now()}.jpg` : `live_selfie_${Date.now()}.jpg`,
      mimeType: 'image/jpeg',
      dataUrlOrBase64: dataUrl,
      sizeBytes: approxBytes,
      isPdf: false,
    };

    if (mode === 'profile') {
      setProfilePhoto(doc);
      setRawProfilePhotoUrl(dataUrl);
      setProfilePhotoCropped(true);
    } else {
      setVerificationSelfie(doc);
      setSelfieCaptureMethod('camera');
    }

    stopCameraStream();
  };

  // Interactive HTML5 Canvas Crop for Profile Photograph
  const applyProfilePhotoCrop = () => {
    if (!rawProfilePhotoUrl) return;
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = 600;
      canvas.height = 600;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      ctx.fillStyle = '#f8fafc';
      ctx.fillRect(0, 0, 600, 600);

      const minDim = Math.min(img.width, img.height);
      const cropSize = minDim / Math.max(1, cropZoom);
      const maxOffsetX = Math.max(0, (img.width - cropSize) / 2);
      const maxOffsetY = Math.max(0, (img.height - cropSize) / 2);

      const sx = (img.width - cropSize) / 2 + (cropOffsetX / 100) * maxOffsetX;
      const sy = (img.height - cropSize) / 2 + (cropOffsetY / 100) * maxOffsetY;

      ctx.drawImage(img, sx, sy, cropSize, cropSize, 0, 0, 600, 600);
      const croppedDataUrl = canvas.toDataURL('image/jpeg', 0.92);
      const approxBytes = Math.round((croppedDataUrl.length * 3) / 4);

      setProfilePhoto({
        fileName: profilePhoto?.fileName || `profile_photo_cropped_${Date.now()}.jpg`,
        mimeType: 'image/jpeg',
        dataUrlOrBase64: croppedDataUrl,
        sizeBytes: approxBytes,
        isPdf: false,
      });
      setProfilePhotoCropped(true);
      setShowCropModal(false);
    };
    img.src = rawProfilePhotoUrl;
  };

  // File Input Handlers
  const handleProfilePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setStepError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await validateClientFile(file, false);
    if (!check.valid || !check.doc) {
      setStepError(check.error || 'Invalid profile photograph.');
      e.target.value = '';
      return;
    }
    setProfilePhoto(check.doc);
    setRawProfilePhotoUrl(check.doc.dataUrlOrBase64);
    setProfilePhotoCropped(false);
    e.target.value = '';
  };

  const handleSelfieFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setStepError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await validateClientFile(file, false);
    if (!check.valid || !check.doc) {
      setStepError(check.error || 'Invalid verification selfie.');
      e.target.value = '';
      return;
    }
    if (profilePhoto && check.doc.dataUrlOrBase64 === profilePhoto.dataUrlOrBase64) {
      setStepError(
        'Please upload or capture a separate live selfie. You cannot reuse the exact same image file uploaded for your profile photograph.'
      );
      e.target.value = '';
      return;
    }
    setVerificationSelfie(check.doc);
    setSelfieCaptureMethod('file_upload');
    e.target.value = '';
  };

  const handleIdDocumentSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setStepError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await validateClientFile(file, true);
    if (!check.valid || !check.doc) {
      setStepError(check.error || 'Invalid identity document file.');
      e.target.value = '';
      return;
    }
    setIdDocumentFile(check.doc);
    e.target.value = '';
  };

  const handleUtilityBillSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    setStepError(null);
    const file = e.target.files?.[0];
    if (!file) return;
    const check = await validateClientFile(file, true);
    if (!check.valid || !check.doc) {
      setStepError(check.error || 'Invalid utility bill file.');
      e.target.value = '';
      return;
    }
    setUtilityBillFile(check.doc);
    e.target.value = '';
  };

  // Step 7: Use My Current Location (Browser GPS + Reverse Geocode)
  const handleUseCurrentLocation = () => {
    setStepError(null);
    setGpsMessage(null);

    if (!navigator.geolocation) {
      setGpsPermissionState('unavailable');
      setGpsMessage(
        'Browser GPS is not supported on this device. Please enter your full service address manually below.'
      );
      return;
    }

    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const lat = Number(pos.coords.latitude.toFixed(6));
        const lng = Number(pos.coords.longitude.toFixed(6));
        const acc = Math.round(pos.coords.accuracy || 20);

        setLatitude(lat);
        setLongitude(lng);
        setGpsAccuracyMeters(acc);
        setGpsPermissionState('granted');
        setPinConfirmed(true);
        setGpsMessage(`GPS coordinates captured (${lat}, ${lng} • accuracy ~${acc}m). Please review the map pin below.`);

        // Attempt reverse geocoding via existing Geoapify backend route
        try {
          const res = await fetch(`/api/location/reverse?lat=${lat}&lon=${lng}`);
          const data = await res.json().catch(() => ({}));
          const loc = data?.location || data?.result;
          if (loc) {
            if (loc.state) {
              const matchedState = NIGERIAN_STATES.find(
                (s) => s.name.toLowerCase() === String(loc.state).toLowerCase()
              );
              if (matchedState) setServiceState(matchedState.name);
            }
            if (loc.city || loc.town || loc.suburb) {
              setServiceTown(loc.city || loc.town || loc.suburb);
            }
            if (loc.formatted || loc.street) {
              setServiceStreetAddress(loc.street || loc.formatted);
            }
          }
        } catch {
          // Reverse geocode is a convenience helper; user can edit address fields directly
        } finally {
          setGpsLoading(false);
        }
      },
      (err) => {
        setGpsLoading(false);
        if (err.code === err.PERMISSION_DENIED) {
          setGpsPermissionState('denied');
          setGpsMessage(
            'Location permission was denied. No problem — please enter your full service address manually below.'
          );
        } else {
          setGpsPermissionState('unavailable');
          setGpsMessage(
            `Could not acquire GPS signal (${err.message || 'Timeout'}). Please enter your service address manually below.`
          );
        }
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      }
    );
  };

  const handleAdjustPinByDelta = (dLat: number, dLng: number) => {
    if (latitude === null || longitude === null) return;
    setLatitude(Number((latitude + dLat).toFixed(6)));
    setLongitude(Number((longitude + dLng).toFixed(6)));
    setPinAdjustedOnMap(true);
    setPinConfirmed(true);
  };

  // Validate Current Step before advancing
  const validateStep = (step: number): boolean => {
    setStepError(null);

    if (step === 1) {
      if (fullName.trim().length < 3) {
        setStepError('Please enter your full legal name (minimum 3 characters).');
        return false;
      }
      if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
        setStepError('Please enter a valid email address.');
        return false;
      }
      const formattedPhone = formatPhoneWithCountryCode(phoneLocal);
      if (formattedPhone.length < 13 || formattedPhone.length > 16) {
        setStepError('Please enter a valid Nigerian phone number (e.g. 0803 123 4567).');
        return false;
      }
      if (!state || !lga) {
        setStepError('Please select your residential State and Local Government Area (LGA).');
        return false;
      }
      if (!town.trim()) {
        setStepError('Please enter your town or neighborhood.');
        return false;
      }
      if (residentialAddress.trim().length < 5) {
        setStepError('Please enter your complete residential street address.');
        return false;
      }
      if (!emailVerifiedViaOtp || !phoneVerifiedViaOtp || !verificationProofToken) {
        setStepError('Please verify your contact details with the 6-digit OTP code sent to your Email and Phone SMS before proceeding.');
        return false;
      }
      return true;
    }

    if (step === 2) {
      if (!primaryCategory) {
        setStepError('Please select your primary trade or service category.');
        return false;
      }
      if (yearsExperience < 0 || yearsExperience > 65) {
        setStepError('Please enter valid years of professional experience (0–65).');
        return false;
      }
      if (professionalDescription.trim().length < 20) {
        setStepError(
          'Please write a clear professional description of your experience and services (minimum 20 characters).'
        );
        return false;
      }
      if (serviceCoverageAreas.length === 0) {
        setStepError('Please select at least one LGA / area where you provide services.');
        return false;
      }
      if (matchingRadiusKm < 1 || matchingRadiusKm > 150) {
        setStepError('Preferred job-matching radius must be between 1 km and 150 km.');
        return false;
      }
      return true;
    }

    if (step === 3) {
      if (!profilePhoto) {
        setStepError(
          'A clear profile photograph is mandatory. Please upload a photo from your device or take one with your camera.'
        );
        return false;
      }
      return true;
    }

    if (step === 4) {
      if (!verificationSelfie) {
        setStepError(
          'A separate verification selfie is mandatory. Please take a selfie with your front camera or upload a current selfie.'
        );
        return false;
      }
      if (profilePhoto && verificationSelfie.dataUrlOrBase64 === profilePhoto.dataUrlOrBase64) {
        setStepError(
          'Your verification selfie must be a separate photograph from your profile photo.'
        );
        return false;
      }
      return true;
    }

    if (step === 5) {
      if (!idDocumentType) {
        setStepError('Please select your National Identity Document type.');
        return false;
      }
      if (!idDocumentFile) {
        setStepError('Please upload a clear image or PDF of your National Identity Document.');
        return false;
      }
      return true;
    }

    if (step === 6) {
      if (!utilityBillType) {
        setStepError('Please select your Utility Bill document type.');
        return false;
      }
      if (!utilityBillFile) {
        setStepError('Please upload a recent electricity bill or accepted utility bill for address verification.');
        return false;
      }
      return true;
    }

    if (step === 7) {
      if (!locationType) {
        setStepError('Please select whether your service location is Home, Business premises, or Both.');
        return false;
      }
      if (!serviceState || !serviceLga || !serviceTown.trim()) {
        setStepError('Please provide your service State, LGA, and Town.');
        return false;
      }
      if (serviceStreetAddress.trim().length < 5) {
        setStepError('Please provide your full service street address.');
        return false;
      }
      if (gpsPermissionState === 'granted' && latitude !== null && !pinConfirmed) {
        setStepError('Please confirm your GPS location pin on the map preview before proceeding.');
        return false;
      }
      return true;
    }

    return true;
  };

  const handleNextStep = () => {
    stopCameraStream();
    if (validateStep(currentStep)) {
      setCurrentStep((prev) => Math.min(8, prev + 1));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevStep = () => {
    stopCameraStream();
    setStepError(null);
    setCurrentStep((prev) => Math.max(1, prev - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const buildSubmissionPayload = () => {
    const selectedCat = SERVICE_CATEGORIES.find((c) => c.slug === primaryCategory);
    return {
      fullName: fullName.trim(),
      email: email.trim().toLowerCase(),
      phone: formatPhoneWithCountryCode(phoneLocal),
      countryCode: '+234',
      emailVerifiedViaOtp,
      phoneVerifiedViaOtp,
      registrationSessionId,
      verificationProofToken,
      state,
      lga,
      town: town.trim(),
      residentialAddress: residentialAddress.trim(),
      preferredContactMethod,
      primaryCategory,
      primaryCategoryName: selectedCat?.name || primaryCategory,
      secondaryCategories,
      additionalSkills,
      yearsExperience: Number(yearsExperience),
      businessName: businessName.trim() || `${fullName.trim()} Services`,
      professionalDescription: professionalDescription.trim(),
      serviceType,
      serviceCoverageAreas,
      matchingRadiusKm: Number(matchingRadiusKm),
      hourlyRate: Number(hourlyRate) || 8000,
      profilePhoto,
      profilePhotoCropped,
      verificationSelfie,
      selfieCaptureMethod,
      idDocumentType,
      idDocumentFile,
      maskedIdReference: maskedIdReference.trim(),
      utilityBillType,
      utilityBillFile,
      utilityBillIssueMonth: utilityBillIssueMonth.trim() || 'Recent (Within 3 Months)',
      locationType,
      gpsPermissionState,
      latitude,
      longitude,
      gpsAccuracyMeters,
      pinAdjustedOnMap,
      pinConfirmed,
      serviceState,
      serviceLga,
      serviceTown: serviceTown.trim(),
      serviceStreetAddress: serviceStreetAddress.trim(),
      consentAccepted,
      googleUserId: isGoogleProOnboarding ? pendingGoogleAuth?.googleUser?.id : undefined,
    };
  };

  const handleSubmitApplication = async (isRetry = false) => {
    setSubmissionError(null);
    setStepError(null);

    if (!consentAccepted) {
      setStepError(
        'Please check the consent box confirming that your information and uploaded verification documents are authentic.'
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const endpoint = isRetry && retryToken ? '/api/pro-applications/retry' : '/api/pro-applications/submit';
      const bodyPayload =
        isRetry && retryToken
          ? { retryToken, payload: buildSubmissionPayload() }
          : buildSubmissionPayload();

      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(bodyPayload),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok || !data.success) {
        if (data.retryToken) {
          setRetryToken(data.retryToken);
        }
        setSubmissionError(
          data.error ||
            'Your application could not be submitted at this time. Please check your connection and click Retry below.'
        );
        return;
      }

      // Clear sensitive binary documents from browser memory immediately after confirmed submission
      setProfilePhoto(null);
      setRawProfilePhotoUrl(null);
      setVerificationSelfie(null);
      setIdDocumentFile(null);
      setUtilityBillFile(null);
      setRetryToken(null);

      try {
        sessionStorage.removeItem('jobmatch_pro_wizard_draft');
      } catch {
        // Ignore storage errors
      }

      setSubmittedSuccess({
        applicationReference: data.applicationReference,
        submittedAt: data.submittedAt || new Date().toISOString(),
      });
      if (isGoogleProOnboarding) {
        setPendingGoogleAuth(null);
      }
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setSubmissionError(
        err?.message ||
          'Network error while submitting your application dossier. Please check your connection and click Retry.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // SUBMISSION CONFIRMATION PAGE
  // ============================================================================
  if (submittedSuccess) {
    return (
      <div
        id="pro-application-confirmation"
        className="bg-white rounded-3xl border border-emerald-200 shadow-xl p-6 sm:p-10 text-slate-900 space-y-6 animate-in fade-in"
      >
        <div className="w-16 h-16 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto shadow-xs">
          <CheckCircle2 className="w-9 h-9" />
        </div>

        <div className="text-center space-y-2">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs font-extrabold uppercase tracking-wider">
            Application Received &bull; Pending Verification
          </span>
          <h2 className="text-2xl sm:text-3xl font-black text-slate-900">
            Application Received!
          </h2>
          <p className="text-sm text-slate-600 max-w-xl mx-auto leading-relaxed">
            Your complete 7-step professional registration and verification dossier has been securely recorded in our verification database and submitted for compliance review.
          </p>
        </div>

        <div className="bg-slate-50 rounded-2xl p-5 border border-slate-200 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-3 border-b border-slate-200">
            <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">
              Application Reference
            </span>
            <span
              id="application-reference-code"
              className="font-mono text-base sm:text-lg font-black text-emerald-700 bg-emerald-50 px-3 py-1 rounded-lg border border-emerald-200"
            >
              {submittedSuccess.applicationReference}
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
            <div>
              <span className="text-slate-500 block">Applicant Name:</span>
              <strong className="text-slate-900">{fullName}</strong>
            </div>
            <div>
              <span className="text-slate-500 block">Primary Trade Category:</span>
              <strong className="text-slate-900 capitalize">{primaryCategory.replace(/-/g, ' ')}</strong>
            </div>
            <div>
              <span className="text-slate-500 block">Verification Status:</span>
              <strong className="text-amber-700">Pending Administrator Review</strong>
            </div>
            <div>
              <span className="text-slate-500 block">Registration Record:</span>
              <strong className="text-emerald-800">Saved in Database (Pending Review)</strong>
            </div>
          </div>
        </div>

        <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-950 space-y-2 leading-relaxed">
          <p className="font-extrabold flex items-center gap-1.5 text-amber-900">
            <Info className="w-4 h-4 text-amber-700 shrink-0" />
            <span>What Happens Next?</span>
          </p>
          <ul className="list-disc pl-5 space-y-1 text-amber-900">
            <li>Our verification team will review your Profile Photograph, Verification Selfie, National Identity Document, Utility Bill, and Service Location.</li>
            <li>Your submitted documents and registration details are securely recorded in the verification database under strict administrative access controls.</li>
            <li>Once approved by an authorized administrator, your verified professional profile will be activated and you can sign in to view matched jobs, submit quotes, and receive payments.</li>
          </ul>
        </div>

        <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            type="button"
            onClick={onSwitchToLogin}
            className="w-full sm:w-auto px-6 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer transition-colors"
          >
            <span>Return to Professional Sign In</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  // ============================================================================
  // MULTI-STEP WIZARD FORM
  // ============================================================================
  return (
    <div className="bg-white rounded-3xl border border-slate-200/90 shadow-xl overflow-hidden text-slate-900">
      {/* Top Progress Header */}
      <div className="bg-slate-900 text-white p-5 sm:p-6 border-b border-slate-800">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <div>
            <span className="inline-flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wider text-emerald-400">
              <ShieldCheck className="w-3.5 h-3.5" />
              Step {currentStep} of 8 &bull; {STEP_DEFINITIONS[currentStep - 1].title}
            </span>
            <h2 className="text-lg sm:text-xl font-black text-white mt-0.5">
              Professional Registration &amp; Verification
            </h2>
          </div>
          <span className="px-2.5 py-1 rounded-lg bg-slate-800 border border-slate-700 text-[11px] font-semibold text-slate-300">
            {Math.round((currentStep / 8) * 100)}% Complete
          </span>
        </div>

        {/* Step Indicator Pills */}
        <div className="grid grid-cols-4 sm:grid-cols-8 gap-1.5">
          {STEP_DEFINITIONS.map((s) => {
            const Icon = s.icon;
            const isCompleted = currentStep > s.number;
            const isActive = currentStep === s.number;
            return (
              <button
                key={s.number}
                type="button"
                onClick={() => {
                  if (s.number < currentStep) {
                    stopCameraStream();
                    setStepError(null);
                    setCurrentStep(s.number);
                  }
                }}
                disabled={s.number > currentStep}
                className={`flex flex-col items-center p-2 rounded-xl border text-center transition-all ${
                  isActive
                    ? 'bg-emerald-600 border-emerald-400 text-white shadow-sm'
                    : isCompleted
                      ? 'bg-emerald-950/80 border-emerald-700/60 text-emerald-300 cursor-pointer hover:bg-emerald-900/60'
                      : 'bg-slate-800/60 border-slate-700/60 text-slate-500 cursor-not-allowed'
                }`}
              >
                <div className="flex items-center gap-1 text-[10px] font-extrabold">
                  {isCompleted ? <Check className="w-3 h-3 text-emerald-400" /> : <Icon className="w-3 h-3" />}
                  <span>{s.number}</span>
                </div>
                <span className="text-[10px] font-medium truncate w-full mt-0.5">{s.short}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Form Content Body */}
      <div className="p-5 sm:p-8 space-y-6">
        {/* Validation Error Alert */}
        {stepError && (
          <div
            role="alert"
            className="p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-start gap-2.5 animate-in fade-in"
          >
            <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-extrabold">Please check the following requirement:</p>
              <p className="mt-0.5 leading-relaxed">{stepError}</p>
            </div>
          </div>
        )}

        {/* ====================================================================
            STEP 1: PERSONAL DETAILS
           ==================================================================== */}
        {currentStep === 1 && (
          <div className="space-y-5" id="pro-step-1-personal">
            {!isGoogleProOnboarding ? (
              <div className="space-y-4">
                <GoogleSignInButton
                  onClick={handleGoogleProRegisterClick}
                  loading={googleLoading}
                  label="Continue with Google"
                />
                <div className="relative flex items-center justify-center">
                  <div className="border-t border-slate-200 w-full" />
                  <span className="bg-white px-3 text-[11px] font-semibold text-slate-400 uppercase tracking-wider shrink-0">
                    or register with email &amp; phone
                  </span>
                  <div className="border-t border-slate-200 w-full" />
                </div>
              </div>
            ) : (
              pendingGoogleAuth?.googleUser && (
                <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-950 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 font-extrabold text-emerald-900">
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                      <span>Google Email Verified ({pendingGoogleAuth.googleUser.email})</span>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setPendingGoogleAuth(null);
                        setEmailVerifiedViaOtp(false);
                        setActiveVerificationStage('email');
                      }}
                      className="text-[11px] text-slate-500 hover:text-slate-800 underline cursor-pointer"
                    >
                      Use Different Email
                    </button>
                  </div>
                  <p className="text-[11px] text-emerald-800 leading-relaxed">
                    Your Google email is verified. To complete your Professional application, you must still verify your{' '}
                    <strong>Nigerian phone number via SMS OTP (Stage 4)</strong> and complete all 8 verification steps (Trade details, Profile Photo, Live Selfie, National ID, Utility Bill, and Service Location) for Administrator Approval.
                  </p>
                  <div className="pt-1.5 flex items-center justify-between border-t border-emerald-200/70 text-[11px]">
                    <span className="text-emerald-800 font-medium">Looking to hire a professional instead?</span>
                    <button
                      type="button"
                      onClick={() => selectRoleForGoogleUser('customer')}
                      className="font-extrabold text-emerald-700 hover:text-emerald-900 underline cursor-pointer"
                    >
                      Switch to Customer Registration →
                    </button>
                  </div>
                </div>
              )
            )}

            <div className="p-3.5 rounded-2xl bg-emerald-50/80 border border-emerald-200 text-xs text-emerald-950 flex items-start gap-2.5">
              <Lock className="w-4 h-4 text-emerald-700 shrink-0 mt-0.5" />
              <div className="leading-relaxed">
                <strong>Registration &amp; Privacy Notice:</strong> Your registration information and supporting documents are securely recorded and processed for account registration, identity/address verification, fraud prevention, and professional eligibility under strict administrative access controls and retention policies.
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Full Legal Name (as shown on your ID) *
                </label>
                <input
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="e.g. Chukwudi Emmanuel Okafor"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Email Address *{' '}
                  {isGoogleProOnboarding && (
                    <span className="text-emerald-600 font-extrabold">(Verified by Google ✓)</span>
                  )}
                </label>
                <input
                  type="email"
                  required
                  readOnly={isGoogleProOnboarding}
                  value={email}
                  onChange={(e) => {
                    if (isGoogleProOnboarding) return;
                    setEmail(e.target.value);
                    if (emailVerifiedViaOtp || phoneVerifiedViaOtp || emailOtpSent) {
                      setEmailVerifiedViaOtp(false);
                      setPhoneVerifiedViaOtp(false);
                      setVerificationProofToken('');
                      setEmailOtpSent(false);
                      setPhoneOtpSent(false);
                      setActiveVerificationStage('email');
                      setOtpCode('');
                      setOtpStatusMessage('Email address changed. Please verify your new email address and phone number.');
                    }
                  }}
                  placeholder="you@example.com"
                  className={`w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600 ${
                    isGoogleProOnboarding ? 'bg-slate-50 text-slate-600 cursor-not-allowed' : ''
                  }`}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Phone Number (with +234 Country Code) *
                </label>
                <div className="flex rounded-xl border border-slate-300 overflow-hidden focus-within:border-emerald-600">
                  <span className="px-3 py-2.5 bg-slate-100 text-slate-700 font-bold text-xs flex items-center border-r border-slate-300 select-none">
                    🇳🇬 +234
                  </span>
                  <input
                    type="tel"
                    required
                    value={phoneLocal}
                    onChange={(e) => {
                      setPhoneLocal(e.target.value);
                      if (phoneVerifiedViaOtp || phoneOtpSent) {
                        setPhoneVerifiedViaOtp(false);
                        setVerificationProofToken('');
                        setPhoneOtpSent(false);
                        setActiveVerificationStage(emailVerifiedViaOtp ? 'phone' : 'email');
                        setOtpCode('');
                        setOtpStatusMessage('Phone number changed. Please verify your new phone number via SMS OTP.');
                      }
                    }}
                    placeholder="803 123 4567"
                    className="w-full px-3 py-2.5 text-sm focus:outline-hidden"
                  />
                </div>
              </div>
            </div>

            {/* Unified Email & Phone SMS OTP Verification Box */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3.5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                <div>
                  <span className="text-xs font-extrabold text-slate-800 flex items-center gap-1.5">
                    <ShieldCheck className="w-4 h-4 text-emerald-600" />
                    Unified Email &amp; Phone SMS Verification
                  </span>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    We send the exact same 6-digit verification code to both your Email and Phone via SMS. Enter the code from whichever arrives first.
                  </p>
                </div>
                {emailVerifiedViaOtp && phoneVerifiedViaOtp ? (
                  <span className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl bg-emerald-100 text-emerald-800 font-bold text-xs">
                    <CheckCircle2 className="w-3.5 h-3.5" /> Email &amp; Phone Verified
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => handleSendVerificationOtp('email')}
                    disabled={otpSending || otpCooldown > 0}
                    className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer disabled:opacity-50 shrink-0"
                  >
                    {otpSending
                      ? 'Sending OTP to Email & SMS...'
                      : otpCooldown > 0
                        ? `Resend Code (${otpCooldown}s)`
                        : emailOtpSent || phoneOtpSent
                          ? 'Resend Code (Email & SMS)'
                          : 'Send Verification Code (Email & SMS)'}
                  </button>
                )}
              </div>

              {/* Channel Delivery Badges */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
                <div
                  className={`p-2.5 rounded-xl border flex items-center justify-between ${
                    emailVerifiedViaOtp && phoneVerifiedViaOtp
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}
                >
                  <span className="font-bold flex items-center gap-1.5">
                    <Mail className="w-3.5 h-3.5 text-emerald-600" />
                    Email Channel
                  </span>
                  <span className="font-extrabold text-[11px]">
                    {emailVerifiedViaOtp && phoneVerifiedViaOtp ? 'Verified ✓' : emailOtpSent ? 'Same Code Sent' : 'Ready'}
                  </span>
                </div>
                <div
                  className={`p-2.5 rounded-xl border flex items-center justify-between ${
                    emailVerifiedViaOtp && phoneVerifiedViaOtp
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                      : 'bg-white border-slate-200 text-slate-700'
                  }`}
                >
                  <span className="font-bold flex items-center gap-1.5">
                    <Phone className="w-3.5 h-3.5 text-emerald-600" />
                    Phone SMS Channel
                  </span>
                  <span className="font-extrabold text-[11px]">
                    {emailVerifiedViaOtp && phoneVerifiedViaOtp ? 'Verified ✓' : phoneOtpSent || emailOtpSent ? 'Same Code Sent' : 'Ready'}
                  </span>
                </div>
              </div>

              {otpStatusMessage && (
                <p className="text-xs text-emerald-800 bg-emerald-50/80 border border-emerald-200 rounded-xl px-3 py-2">
                  {otpStatusMessage}
                </p>
              )}

              {!(emailVerifiedViaOtp && phoneVerifiedViaOtp) && (emailOtpSent || phoneOtpSent) && (
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 pt-1">
                  <input
                    type="text"
                    inputMode="numeric"
                    maxLength={6}
                    value={otpCode}
                    onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="Enter 6-digit OTP (Email or SMS)"
                    className="px-3.5 py-2 rounded-xl border border-slate-300 text-sm font-mono tracking-widest sm:w-64 bg-white"
                  />
                  <button
                    type="button"
                    onClick={handleVerifyOtp}
                    disabled={otpVerifying || otpCode.replace(/\D/g, '').length !== 6}
                    className="px-4 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs cursor-pointer disabled:opacity-50"
                  >
                    {otpVerifying ? 'Verifying...' : 'Verify Code'}
                  </button>
                </div>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">State *</label>
                <select
                  value={state}
                  onChange={(e) => {
                    const nextState = e.target.value;
                    setState(nextState);
                    const nextLgas = NIGERIAN_LGAS_MAP[nextState] || [];
                    if (nextLgas.length > 0) {
                      setLga(nextLgas[0]);
                      setServiceState(nextState);
                      setServiceLga(nextLgas[0]);
                      setServiceCoverageAreas([nextLgas[0]]);
                    }
                  }}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {NIGERIAN_STATES.map((st) => (
                    <option key={st.id} value={st.name}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Local Government Area (LGA) *
                </label>
                <select
                  value={lga}
                  onChange={(e) => {
                    setLga(e.target.value);
                    if (!serviceCoverageAreas.includes(e.target.value)) {
                      setServiceCoverageAreas((prev) => [...prev, e.target.value]);
                    }
                  }}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {availableResidentialLgas.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Town / Neighborhood *
                </label>
                <input
                  type="text"
                  required
                  value={town}
                  onChange={(e) => setTown(e.target.value)}
                  placeholder="e.g. Allen Avenue / Opebi"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Residential Street Address *
              </label>
              <input
                type="text"
                required
                value={residentialAddress}
                onChange={(e) => setResidentialAddress(e.target.value)}
                placeholder="House number, street name, and nearest landmark"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Preferred Contact Method *
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                {(
                  [
                    { id: 'phone', label: 'Phone Call' },
                    { id: 'whatsapp', label: 'WhatsApp' },
                    { id: 'email', label: 'Email' },
                    { id: 'sms', label: 'SMS Text' },
                  ] as const
                ).map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setPreferredContactMethod(opt.id)}
                    className={`px-3 py-2.5 rounded-xl border text-xs font-bold transition-all cursor-pointer ${
                      preferredContactMethod === opt.id
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ====================================================================
            STEP 2: PROFESSIONAL DETAILS
           ==================================================================== */}
        {currentStep === 2 && (
          <div className="space-y-5" id="pro-step-2-professional">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Primary Trade or Service Category *
                </label>
                <select
                  value={primaryCategory}
                  onChange={(e) => setPrimaryCategory(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {SERVICE_CATEGORIES.map((cat) => (
                    <option key={cat.id} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Years of Professional Experience *
                </label>
                <input
                  type="number"
                  min={0}
                  max={65}
                  required
                  value={yearsExperience}
                  onChange={(e) => setYearsExperience(Number(e.target.value))}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Business or Trading Name (Optional)
                </label>
                <input
                  type="text"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. Okafor Master Plumbing & Borehole Works"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>
            </div>

            {/* Additional Skills & Secondary Trades */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Additional Skills &amp; Specialties
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const val = skillInput.trim();
                      if (val && !additionalSkills.includes(val)) {
                        setAdditionalSkills((prev) => [...prev, val]);
                        setSkillInput('');
                      }
                    }
                  }}
                  placeholder="e.g. Submersible pump installation, Inverter wiring, POP screeding"
                  className="flex-1 px-3.5 py-2 rounded-xl border border-slate-300 text-xs focus:outline-hidden focus:border-emerald-600"
                />
                <button
                  type="button"
                  onClick={() => {
                    const val = skillInput.trim();
                    if (val && !additionalSkills.includes(val)) {
                      setAdditionalSkills((prev) => [...prev, val]);
                      setSkillInput('');
                    }
                  }}
                  className="px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-xs cursor-pointer"
                >
                  Add Skill
                </button>
              </div>
              {additionalSkills.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {additionalSkills.map((sk) => (
                    <span
                      key={sk}
                      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-xs font-semibold"
                    >
                      {sk}
                      <button
                        type="button"
                        onClick={() => setAdditionalSkills((prev) => prev.filter((item) => item !== sk))}
                        className="text-emerald-700 hover:text-rose-600 cursor-pointer"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Service Type */}
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Service Operating Type *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {(
                  [
                    {
                      id: 'mobile',
                      title: 'Mobile Service',
                      desc: 'I travel to customer homes & offices',
                    },
                    {
                      id: 'business_based',
                      title: 'Business Premises / Workshop',
                      desc: 'I operate from a dedicated shop or office',
                    },
                    {
                      id: 'home_based',
                      title: 'Home-Based Service',
                      desc: 'I work from my home workshop & visit clients',
                    },
                  ] as const
                ).map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => setServiceType(item.id)}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      serviceType === item.id
                        ? 'bg-emerald-50 border-emerald-600 ring-1 ring-emerald-600'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-xs font-extrabold text-slate-900">{item.title}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{item.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Service Coverage LGAs & Matching Radius */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Service Coverage LGAs ({state}) *
                </label>
                <div className="max-h-36 overflow-y-auto p-2.5 rounded-xl border border-slate-300 bg-slate-50 grid grid-cols-2 gap-1.5">
                  {availableResidentialLgas.map((areaLga) => {
                    const checked = serviceCoverageAreas.includes(areaLga);
                    return (
                      <label
                        key={areaLga}
                        className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer select-none"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            if (checked) {
                              setServiceCoverageAreas((prev) => prev.filter((a) => a !== areaLga));
                            } else {
                              setServiceCoverageAreas((prev) => [...prev, areaLga]);
                            }
                          }}
                          className="rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="truncate">{areaLga}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Preferred Job-Matching Radius: <strong className="text-emerald-700">{matchingRadiusKm} km</strong> *
                </label>
                <input
                  type="range"
                  min={2}
                  max={100}
                  value={matchingRadiusKm}
                  onChange={(e) => setMatchingRadiusKm(Number(e.target.value))}
                  className="w-full accent-emerald-600 cursor-pointer mt-2"
                />
                <div className="flex justify-between text-[11px] text-slate-500 mt-1">
                  <span>2 km (Immediate Area)</span>
                  <span>25 km (Metro)</span>
                  <span>100 km (Statewide)</span>
                </div>
              </div>
            </div>

            {/* Professional Description */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Professional Description &amp; Summary of Experience *
                </label>
                <span className="text-[11px] text-slate-500">
                  {professionalDescription.trim().length} / 2500 chars (min 20)
                </span>
              </div>
              <textarea
                rows={4}
                required
                value={professionalDescription}
                onChange={(e) => setProfessionalDescription(e.target.value)}
                placeholder="Describe your trade qualifications, typical projects, diagnostic tools, and customer service standards..."
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
              />
            </div>
          </div>
        )}

        {/* ====================================================================
            STEP 3: MANDATORY PROFILE PHOTOGRAPH
           ==================================================================== */}
        {currentStep === 3 && (
          <div className="space-y-5" id="pro-step-3-profile-photo">
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-950 space-y-1">
              <p className="font-extrabold text-emerald-900 flex items-center gap-1.5">
                <Camera className="w-4 h-4 text-emerald-700" />
                Mandatory Public Profile Photograph
              </p>
              <p className="leading-relaxed">
                Upload or capture a clear, professional head-and-shoulders photograph of yourself. <strong>This photograph is intended to become your public profile photograph on JobMatch NG after administrator approval.</strong>
              </p>
            </div>

            {/* Live Camera View for Profile Photo */}
            {activeCameraMode === 'profile' && (
              <div className="p-4 rounded-2xl bg-slate-900 text-white space-y-3 text-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-64 h-64 object-cover rounded-2xl mx-auto border-2 border-emerald-400"
                />
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => captureFrameFromVideo('profile')}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs cursor-pointer"
                  >
                    Capture Profile Photo
                  </button>
                  <button
                    type="button"
                    onClick={stopCameraStream}
                    className="px-4 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-bold text-xs cursor-pointer"
                  >
                    Cancel Camera
                  </button>
                </div>
              </div>
            )}

            {cameraError && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                {cameraError}
              </div>
            )}

            {/* Preview & Controls */}
            {profilePhoto ? (
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center gap-5">
                <img
                  src={profilePhoto.dataUrlOrBase64}
                  alt="Applicant Profile Photograph Preview"
                  className="w-36 h-36 rounded-2xl object-cover border-2 border-emerald-500 shadow-md shrink-0"
                />
                <div className="flex-1 space-y-3 text-center sm:text-left">
                  <div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                      <CheckCircle2 className="w-3 h-3" /> Validated Profile Photo Ready
                    </span>
                    <p className="text-xs font-bold text-slate-800 mt-1">{profilePhoto.fileName}</p>
                    <p className="text-[11px] text-slate-500">
                      Format: {profilePhoto.mimeType} &bull; Size: {(profilePhoto.sizeBytes / 1024).toFixed(1)} KB &bull;{' '}
                      {profilePhotoCropped ? 'Cropped & Framed' : 'Original Aspect'}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                    <button
                      type="button"
                      onClick={() => setShowCropModal(true)}
                      className="px-3.5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer"
                    >
                      <Crop className="w-3.5 h-3.5" /> Crop / Adjust Photo
                    </button>
                    <button
                      type="button"
                      onClick={() => startCamera('profile')}
                      className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer"
                    >
                      <Camera className="w-3.5 h-3.5" /> Retake with Camera
                    </button>
                    <label className="px-3.5 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs flex items-center gap-1.5 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Replace File
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        onChange={handleProfilePhotoSelect}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => {
                        setProfilePhoto(null);
                        setRawProfilePhotoUrl(null);
                      }}
                      className="px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/70 text-center space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                  <Camera className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-slate-800">
                    Upload or Take Your Profile Photograph
                  </p>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    Accepted formats: JPEG, PNG, or WebP (max 5 MB). Clear face visibility, no sunglasses or face coverings.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <label className="w-full sm:w-auto px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-xs">
                    <Upload className="w-4 h-4" />
                    <span>Select Photo from Device / Phone Camera</span>
                    <input
                      id="profile-photo-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="user"
                      onChange={handleProfilePhotoSelect}
                      className="hidden"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => startCamera('profile')}
                    className="w-full sm:w-auto px-5 py-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Use Live Camera</span>
                  </button>
                </div>
              </div>
            )}

            {/* Interactive Crop Modal */}
            {showCropModal && rawProfilePhotoUrl && (
              <div className="p-5 rounded-2xl bg-slate-900 text-white space-y-4 border border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-extrabold uppercase tracking-wider text-emerald-400 flex items-center gap-1.5">
                    <Crop className="w-4 h-4" /> Crop &amp; Frame Your Profile Photo
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowCropModal(false)}
                    className="text-slate-400 hover:text-white cursor-pointer"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
                  <div>
                    <label className="block text-slate-300 mb-1">Zoom ({cropZoom.toFixed(1)}x)</label>
                    <input
                      type="range"
                      min={1}
                      max={2.5}
                      step={0.1}
                      value={cropZoom}
                      onChange={(e) => setCropZoom(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 mb-1">Horizontal Frame ({cropOffsetX}%)</label>
                    <input
                      type="range"
                      min={-100}
                      max={100}
                      value={cropOffsetX}
                      onChange={(e) => setCropOffsetX(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="block text-slate-300 mb-1">Vertical Frame ({cropOffsetY}%)</label>
                    <input
                      type="range"
                      min={-100}
                      max={100}
                      value={cropOffsetY}
                      onChange={(e) => setCropOffsetY(Number(e.target.value))}
                      className="w-full accent-emerald-500"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowCropModal(false)}
                    className="px-4 py-2 rounded-xl bg-slate-800 text-slate-300 text-xs font-bold cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={applyProfilePhotoCrop}
                    className="px-5 py-2 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-bold cursor-pointer"
                  >
                    Apply Crop
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====================================================================
            STEP 4: MANDATORY VERIFICATION SELFIE
           ==================================================================== */}
        {currentStep === 4 && (
          <div className="space-y-5" id="pro-step-4-selfie">
            <div className="p-4 rounded-2xl bg-amber-50 border border-amber-200 text-xs text-amber-950 space-y-1">
              <p className="font-extrabold text-amber-900 flex items-center gap-1.5">
                <User className="w-4 h-4 text-amber-700" />
                Mandatory Verification Selfie (Separate from Profile Photo)
              </p>
              <p className="leading-relaxed">
                Please take a current selfie looking directly into your front-facing camera in good lighting. <strong>This selfie is collected as verification evidence for manual reviewer comparison and is not treated as automated biometric proof of identity by itself.</strong>
              </p>
            </div>

            {activeCameraMode === 'selfie' && (
              <div className="p-4 rounded-2xl bg-slate-900 text-white space-y-3 text-center">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-64 h-64 object-cover rounded-2xl mx-auto border-2 border-emerald-400"
                />
                <div className="flex items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => captureFrameFromVideo('selfie')}
                    className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs cursor-pointer"
                  >
                    Take Verification Selfie
                  </button>
                  <button
                    type="button"
                    onClick={stopCameraStream}
                    className="px-4 py-2.5 rounded-xl bg-slate-700 hover:bg-slate-600 text-white font-bold text-xs cursor-pointer"
                  >
                    Cancel Camera
                  </button>
                </div>
              </div>
            )}

            {cameraError && (
              <div className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs">
                {cameraError}
              </div>
            )}

            {verificationSelfie ? (
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center gap-5">
                <img
                  src={verificationSelfie.dataUrlOrBase64}
                  alt="Applicant Verification Selfie Preview"
                  className="w-36 h-36 rounded-2xl object-cover border-2 border-emerald-500 shadow-md shrink-0"
                />
                <div className="flex-1 space-y-3 text-center sm:text-left">
                  <div>
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                      <CheckCircle2 className="w-3 h-3" /> Verification Selfie Ready (Evidence for Manual Review)
                    </span>
                    <p className="text-xs font-bold text-slate-800 mt-1">{verificationSelfie.fileName}</p>
                    <p className="text-[11px] text-slate-500">
                      Method: {selfieCaptureMethod === 'camera' ? 'Front-Facing Camera' : 'File Upload Fallback'} &bull; Size:{' '}
                      {(verificationSelfie.sizeBytes / 1024).toFixed(1)} KB
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                    <button
                      type="button"
                      onClick={() => startCamera('selfie')}
                      className="px-3.5 py-2 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer"
                    >
                      <Camera className="w-3.5 h-3.5" /> Retake Selfie with Camera
                    </button>
                    <label className="px-3.5 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs flex items-center gap-1.5 cursor-pointer">
                      <Upload className="w-3.5 h-3.5" /> Replace via File Upload
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        capture="user"
                        onChange={handleSelfieFileSelect}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setVerificationSelfie(null)}
                      className="px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/70 text-center space-y-4">
                <div className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto">
                  <User className="w-7 h-7" />
                </div>
                <div className="space-y-1">
                  <p className="text-sm font-extrabold text-slate-800">
                    Take a Current Verification Selfie
                  </p>
                  <p className="text-xs text-slate-500 max-w-md mx-auto">
                    Use your phone’s front-facing camera or upload a current selfie image (JPEG, PNG, or WebP, max 5 MB).
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <button
                    type="button"
                    onClick={() => startCamera('selfie')}
                    className="w-full sm:w-auto px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shadow-xs"
                  >
                    <Camera className="w-4 h-4" />
                    <span>Open Front-Facing Camera</span>
                  </button>
                  <label className="w-full sm:w-auto px-5 py-3 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-800 font-bold text-xs flex items-center justify-center gap-2 cursor-pointer">
                    <Upload className="w-4 h-4" />
                    <span>Upload Selfie File (Camera Fallback)</span>
                    <input
                      id="selfie-file-input"
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      capture="user"
                      onChange={handleSelfieFileSelect}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ====================================================================
            STEP 5: NATIONAL IDENTITY CARD
           ==================================================================== */}
        {currentStep === 5 && (
          <div className="space-y-5" id="pro-step-5-national-id">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-700 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-extrabold text-slate-900 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" />
                  National Identity Card / Official Government ID
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 font-bold text-[11px]">
                  Status: {idDocumentFile ? 'Attached for Manual Review' : 'Awaiting Upload'}
                </span>
              </div>
              <p className="leading-relaxed">
                <strong>Image Quality Guidance:</strong> Ensure all 4 corners of the document are visible, your full legal name and photograph are sharp and legible, and there is no flash glare. Uploaded identity documents are <strong>not authenticated via an automated NIMC API</strong> and will be inspected manually by our verification team.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Select Identity Document Type *
                </label>
                <select
                  value={idDocumentType}
                  onChange={(e) => setIdDocumentType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {ACCEPTED_ID_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Last 4 Digits of ID (Optional — Do NOT enter full NIN)
                </label>
                <input
                  type="text"
                  maxLength={4}
                  value={maskedIdReference}
                  onChange={(e) => setMaskedIdReference(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 4))}
                  placeholder="e.g. 4821 (Optional)"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>
            </div>

            {idDocumentFile ? (
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center gap-5">
                {idDocumentFile.isPdf ? (
                  <div className="w-28 h-28 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 flex flex-col items-center justify-center shrink-0">
                    <FileText className="w-10 h-10" />
                    <span className="text-[10px] font-extrabold mt-1">PDF DOCUMENT</span>
                  </div>
                ) : (
                  <img
                    src={idDocumentFile.dataUrlOrBase64}
                    alt="Identity Document Preview"
                    className="w-36 h-28 rounded-2xl object-cover border border-slate-300 shrink-0"
                  />
                )}
                <div className="flex-1 space-y-2 text-center sm:text-left">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                    <CheckCircle2 className="w-3 h-3" /> Document Validated &amp; Ready for Email Submission
                  </span>
                  <p className="text-xs font-bold text-slate-800">{idDocumentFile.fileName}</p>
                  <p className="text-[11px] text-slate-500">
                    Type: {idDocumentFile.mimeType} &bull; Size: {(idDocumentFile.sizeBytes / 1024).toFixed(1)} KB
                  </p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
                    <label className="px-3.5 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs flex items-center gap-1.5 cursor-pointer">
                      <RefreshCw className="w-3.5 h-3.5" /> Replace Document
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={handleIdDocumentSelect}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setIdDocumentFile(null)}
                      className="px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/70 text-center space-y-3">
                <ShieldCheck className="w-10 h-10 text-emerald-600 mx-auto" />
                <p className="text-sm font-extrabold text-slate-800">
                  Upload Your Selected Identity Document
                </p>
                <p className="text-xs text-slate-500">
                  Supported formats: JPEG, PNG, WebP image or PDF document (max 5 MB).
                </p>
                <label className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>Select Identity Document (Image or PDF)</span>
                  <input
                    id="id-document-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleIdDocumentSelect}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {/* ====================================================================
            STEP 6: UTILITY BILL (ADDRESS VERIFICATION EVIDENCE)
           ==================================================================== */}
        {currentStep === 6 && (
          <div className="space-y-5" id="pro-step-6-utility-bill">
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 text-xs text-slate-700 space-y-1.5">
              <div className="flex items-center justify-between">
                <span className="font-extrabold text-slate-900 flex items-center gap-1.5">
                  <FileText className="w-4 h-4 text-emerald-600" />
                  Recent Utility Bill (Address Verification Evidence)
                </span>
                <span className="px-2.5 py-0.5 rounded-full bg-amber-100 text-amber-900 font-bold text-[11px]">
                  Status: {utilityBillFile ? 'Attached for Manual Assessment' : 'Awaiting Upload'}
                </span>
              </div>
              <p className="leading-relaxed">
                <strong>Address Verification Notice:</strong> Please upload a recent electricity bill (EKEDC, IKEDC, AEDC, PHED, IBEDC, etc.) or another accepted utility bill showing your residential or workshop address. This document is collected to support address verification and does not automatically confirm residency until assessed by an authorized reviewer.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Select Utility Bill / Address Document Type *
                </label>
                <select
                  value={utilityBillType}
                  onChange={(e) => setUtilityBillType(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {ACCEPTED_UTILITY_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Bill Issue Month / Period (Optional)
                </label>
                <input
                  type="text"
                  value={utilityBillIssueMonth}
                  onChange={(e) => setUtilityBillIssueMonth(e.target.value)}
                  placeholder="e.g.Last 3 Months"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>
            </div>

            {utilityBillFile ? (
              <div className="p-5 rounded-2xl bg-slate-50 border border-slate-200 flex flex-col sm:flex-row items-center gap-5">
                {utilityBillFile.isPdf ? (
                  <div className="w-28 h-28 rounded-2xl bg-rose-50 border border-rose-200 text-rose-700 flex flex-col items-center justify-center shrink-0">
                    <FileText className="w-10 h-10" />
                    <span className="text-[10px] font-extrabold mt-1">PDF BILL</span>
                  </div>
                ) : (
                  <img
                    src={utilityBillFile.dataUrlOrBase64}
                    alt="Utility Bill Preview"
                    className="w-36 h-28 rounded-2xl object-cover border border-slate-300 shrink-0"
                  />
                )}
                <div className="flex-1 space-y-2 text-center sm:text-left">
                  <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-[11px] font-bold">
                    <CheckCircle2 className="w-3 h-3" /> Utility Bill Validated &amp; Ready
                  </span>
                  <p className="text-xs font-bold text-slate-800">{utilityBillFile.fileName}</p>
                  <p className="text-[11px] text-slate-500">
                    Type: {utilityBillFile.mimeType} &bull; Size: {(utilityBillFile.sizeBytes / 1024).toFixed(1)} KB
                  </p>
                  <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 pt-1">
                    <label className="px-3.5 py-2 rounded-xl border border-slate-300 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs flex items-center gap-1.5 cursor-pointer">
                      <RefreshCw className="w-3.5 h-3.5" /> Replace Utility Bill
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/webp,application/pdf"
                        onChange={handleUtilityBillSelect}
                        className="hidden"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setUtilityBillFile(null)}
                      className="px-3 py-2 rounded-xl border border-rose-200 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs flex items-center gap-1 cursor-pointer"
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Remove
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-6 rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/70 text-center space-y-3">
                <FileText className="w-10 h-10 text-emerald-600 mx-auto" />
                <p className="text-sm font-extrabold text-slate-800">
                  Upload Your Recent Utility Bill
                </p>
                <p className="text-xs text-slate-500">
                  Supported formats: JPEG, PNG, WebP image or PDF document (max 5 MB).
                </p>
                <label className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs cursor-pointer">
                  <Upload className="w-4 h-4" />
                  <span>Select Utility Bill (Image or PDF)</span>
                  <input
                    id="utility-bill-file-input"
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={handleUtilityBillSelect}
                    className="hidden"
                  />
                </label>
              </div>
            )}
          </div>
        )}

        {/* ====================================================================
            STEP 7: HOME OR BUSINESS LOCATION & GPS CAPTURE
           ==================================================================== */}
        {currentStep === 7 && (
          <div className="space-y-5" id="pro-step-7-location">
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Select Location Type *
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {(
                  [
                    { id: 'home', label: 'Home Location', desc: 'I operate from my residential address' },
                    { id: 'business', label: 'Business Premises', desc: 'I operate from a commercial shop / workshop' },
                    { id: 'both', label: 'Both Home & Business', desc: 'I use both my home base and workshop' },
                  ] as const
                ).map((loc) => (
                  <button
                    key={loc.id}
                    type="button"
                    onClick={() => setLocationType(loc.id)}
                    className={`p-3 rounded-2xl border text-left transition-all cursor-pointer ${
                      locationType === loc.id
                        ? 'bg-emerald-50 border-emerald-600 ring-1 ring-emerald-600'
                        : 'bg-white border-slate-200 hover:bg-slate-50'
                    }`}
                  >
                    <div className="text-xs font-extrabold text-slate-900">{loc.label}</div>
                    <div className="text-[11px] text-slate-500 mt-0.5">{loc.desc}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* Browser GPS Capture Card */}
            <div className="p-4 rounded-2xl bg-slate-900 text-white space-y-3">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                  <span className="text-xs font-extrabold text-emerald-400 flex items-center gap-1.5">
                    <Navigation className="w-4 h-4" />
                    One-Time Browser GPS Location Capture
                  </span>
                  <p className="text-[11px] text-slate-300 mt-0.5">
                    Captures your current latitude &amp; longitude only when you click the button. We never track your location continuously or substitute IP geolocation.
                  </p>
                </div>
                <button
                  type="button"
                  id="use-current-location-btn"
                  onClick={handleUseCurrentLocation}
                  disabled={gpsLoading}
                  className="px-4 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center justify-center gap-2 cursor-pointer shrink-0 disabled:opacity-50"
                >
                  {gpsLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      <span>Acquiring GPS...</span>
                    </>
                  ) : (
                    <>
                      <MapPin className="w-4 h-4" />
                      <span>Use My Current Location</span>
                    </>
                  )}
                </button>
              </div>

              {gpsMessage && (
                <div className="p-3 rounded-xl bg-slate-800 border border-slate-700 text-xs text-emerald-300">
                  {gpsMessage}
                </div>
              )}

              {/* Interactive Map Preview & Pin Adjustment when GPS Coordinates exist */}
              {latitude !== null && longitude !== null && (
                <div className="p-3.5 rounded-xl bg-slate-800 border border-slate-700 space-y-3">
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs">
                    <span className="font-mono text-emerald-300 font-bold">
                      Pin: {latitude.toFixed(6)}, {longitude.toFixed(6)}
                    </span>
                    <div className="flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => handleAdjustPinByDelta(0.0005, 0)}
                        className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-[11px] cursor-pointer"
                      >
                        Nudge North
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAdjustPinByDelta(-0.0005, 0)}
                        className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-[11px] cursor-pointer"
                      >
                        Nudge South
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAdjustPinByDelta(0, 0.0005)}
                        className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-[11px] cursor-pointer"
                      >
                        Nudge East
                      </button>
                      <button
                        type="button"
                        onClick={() => handleAdjustPinByDelta(0, -0.0005)}
                        className="px-2 py-1 rounded bg-slate-700 hover:bg-slate-600 text-[11px] cursor-pointer"
                      >
                        Nudge West
                      </button>
                    </div>
                  </div>

                  {/* Embedded OpenStreetMap Preview Iframe */}
                  <div className="w-full h-48 rounded-xl overflow-hidden border border-slate-600 bg-slate-950">
                    <iframe
                      title="Service Location Map Preview"
                      width="100%"
                      height="100%"
                      frameBorder="0"
                      scrolling="no"
                      src={`https://www.openstreetmap.org/export/embed.html?bbox=${longitude - 0.005}%2C${latitude - 0.005}%2C${longitude + 0.005}%2C${latitude + 0.005}&layer=mapnik&marker=${latitude}%2C${longitude}`}
                    />
                  </div>

                  <label className="flex items-center gap-2 text-xs text-emerald-200 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={pinConfirmed}
                      onChange={(e) => setPinConfirmed(e.target.checked)}
                      className="rounded border-slate-500 text-emerald-500"
                    />
                    <span>I confirm this map pin accurately reflects my service location.</span>
                  </label>
                </div>
              )}
            </div>

            {/* Full Service Address Fields (Required for both GPS & Manual Entry) */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Service State *
                </label>
                <select
                  value={serviceState}
                  onChange={(e) => {
                    const nextState = e.target.value;
                    setServiceState(nextState);
                    const lgas = NIGERIAN_LGAS_MAP[nextState] || [];
                    if (lgas.length > 0) setServiceLga(lgas[0]);
                  }}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {NIGERIAN_STATES.map((st) => (
                    <option key={st.id} value={st.name}>
                      {st.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Service LGA *
                </label>
                <select
                  value={serviceLga}
                  onChange={(e) => setServiceLga(e.target.value)}
                  className="w-full px-3 py-2.5 rounded-xl border border-slate-300 text-sm bg-white focus:outline-hidden focus:border-emerald-600"
                >
                  {availableServiceLgas.map((item) => (
                    <option key={item} value={item}>
                      {item}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1.5">
                  Service Town / Area *
                </label>
                <input
                  type="text"
                  required
                  value={serviceTown}
                  onChange={(e) => setServiceTown(e.target.value)}
                  placeholder="e.g. Ikeja GRA / Lekki Phase 1"
                  className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Full Service Street Address *
              </label>
              <input
                type="text"
                required
                value={serviceStreetAddress}
                onChange={(e) => setServiceStreetAddress(e.target.value)}
                placeholder="Shop/House number, street name, and nearest bus stop or landmark"
                className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-sm focus:outline-hidden focus:border-emerald-600"
              />
              <p className="text-[11px] text-slate-500 mt-1">
                Privacy Assurance: Exact residential coordinates and private street numbers are never shown publicly to customers before a job booking is confirmed.
              </p>
            </div>
          </div>
        )}

        {/* ====================================================================
            STEP 8: FINAL REVIEW, CONSENT & EMAIL SUBMISSION
           ==================================================================== */}
        {currentStep === 8 && (
          <div className="space-y-5" id="pro-step-8-review">
            <div className="p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-xs text-emerald-950">
              <p className="font-extrabold text-emerald-900">
                Final Application Review — Ready for Email Dispatch
              </p>
              <p className="mt-0.5 leading-relaxed">
                Please verify your details below. When you submit, your complete application and 4 verification files will be sent to our verification team and will not be stored in the JobMatch NG database.
              </p>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1.5">
                <div className="font-extrabold text-slate-900 uppercase tracking-wider text-[11px]">
                  1. Personal Details
                </div>
                <p><span className="text-slate-500">Full Name:</span> <strong>{fullName}</strong></p>
                <p><span className="text-slate-500">Email:</span> <strong>{email}</strong></p>
                <p><span className="text-slate-500">Phone:</span> <strong>{formatPhoneWithCountryCode(phoneLocal)}</strong></p>
                <p><span className="text-slate-500">Residence:</span> {residentialAddress}, {town}, {lga}, {state}</p>
                <p><span className="text-slate-500">Preferred Contact:</span> <span className="uppercase font-bold">{preferredContactMethod}</span></p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1.5">
                <div className="font-extrabold text-slate-900 uppercase tracking-wider text-[11px]">
                  2. Professional Details
                </div>
                <p><span className="text-slate-500">Primary Trade:</span> <strong className="capitalize">{primaryCategory.replace(/-/g, ' ')}</strong></p>
                <p><span className="text-slate-500">Experience:</span> <strong>{yearsExperience} year(s)</strong></p>
                <p><span className="text-slate-500">Business Name:</span> {businessName || `${fullName} Services`}</p>
                <p><span className="text-slate-500">Coverage Areas:</span> {serviceCoverageAreas.join(', ')} ({matchingRadiusKm} km radius)</p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1.5">
                <div className="font-extrabold text-slate-900 uppercase tracking-wider text-[11px]">
                  3. Attached Verification Files (4 Mandatory)
                </div>
                <p><span className="text-slate-500">Profile Photo:</span> <strong>{profilePhoto?.fileName || 'Missing'}</strong></p>
                <p><span className="text-slate-500">Live Selfie:</span> <strong>{verificationSelfie?.fileName || 'Missing'}</strong></p>
                <p><span className="text-slate-500">National ID:</span> <strong>{idDocumentFile?.fileName || 'Missing'}</strong></p>
                <p><span className="text-slate-500">Utility Bill:</span> <strong>{utilityBillFile?.fileName || 'Missing'}</strong></p>
              </div>

              <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-1.5">
                <div className="font-extrabold text-slate-900 uppercase tracking-wider text-[11px]">
                  4. Service Location &amp; GPS
                </div>
                <p><span className="text-slate-500">Location Type:</span> <strong className="uppercase">{locationType}</strong></p>
                <p><span className="text-slate-500">Service Address:</span> {serviceStreetAddress}, {serviceTown}, {serviceLga}, {serviceState}</p>
                <p>
                  <span className="text-slate-500">GPS Coordinates:</span>{' '}
                  {latitude !== null && longitude !== null
                    ? `${latitude}, ${longitude} (${gpsPermissionState})`
                    : `Manual Address Entry (${gpsPermissionState})`}
                </p>
              </div>
            </div>

            {/* Consent Checkbox */}
            <div className="p-4 rounded-2xl bg-slate-50 border border-slate-200 space-y-3 text-xs">
              <label className="flex items-start gap-2.5 cursor-pointer select-none">
                <input
                  id="consent-verification-checkbox"
                  type="checkbox"
                  checked={consentAccepted}
                  onChange={(e) => setConsentAccepted(e.target.checked)}
                  className="mt-0.5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-slate-700 leading-relaxed">
                  I confirm that all information, photographs, identity documents, and utility bills provided are authentic and belong to me, and I consent to identity and address verification by JobMatch NG under applicable data protection policies.
                </span>
              </label>
            </div>

            {/* Submission Error & Safe Retry Banner */}
            {submissionError && (
              <div
                role="alert"
                id="pro-submission-error-banner"
                className="p-4 rounded-2xl bg-rose-50 border border-rose-300 text-rose-950 text-xs space-y-3"
              >
                <div className="flex items-start gap-2.5">
                  <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-extrabold text-rose-900">
                      Application Submission Notice
                    </p>
                    <p className="mt-1 leading-relaxed text-rose-800">{submissionError}</p>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    id="retry-pro-application-btn"
                    onClick={() => handleSubmitApplication(true)}
                    disabled={isSubmitting}
                    className="px-4 py-2 rounded-xl bg-rose-700 hover:bg-rose-800 text-white font-bold text-xs flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${isSubmitting ? 'animate-spin' : ''}`} />
                    <span>Retry Submission</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Navigation Footer */}
        <div className="pt-4 border-t border-slate-200 flex items-center justify-between gap-3">
          {currentStep > 1 ? (
            <button
              type="button"
              onClick={handlePrevStep}
              disabled={isSubmitting}
              className="px-4 py-2.5 rounded-xl border border-slate-300 text-slate-700 font-bold text-xs hover:bg-slate-50 flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
            >
              <ChevronLeft className="w-4 h-4" />
              <span>Previous Step</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="px-4 py-2.5 rounded-xl border border-slate-200 text-slate-600 font-bold text-xs hover:bg-slate-50 cursor-pointer"
            >
              Already Approved? Sign In
            </button>
          )}

          {currentStep < 8 ? (
            <button
              type="button"
              id="pro-wizard-next-btn"
              onClick={handleNextStep}
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs shadow-md flex items-center gap-1.5 cursor-pointer transition-colors"
            >
              <span>Continue to Step {currentStep + 1}</span>
              <ChevronRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              id="pro-wizard-submit-btn"
              onClick={() => handleSubmitApplication(false)}
              disabled={isSubmitting}
              className="px-6 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs shadow-lg flex items-center gap-2 cursor-pointer disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Sending Application Dossier...</span>
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4" />
                  <span>Submit Application &amp; Documents</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};
